import React, {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ClickAwayListener, MenuList, Paper, Popper } from '@mui/material';

import { useDelayedOperation } from '@paragon/client-tools';

import { isTouchEvent } from '../../../three/util/events/touch';
import { DEFAULT_TOOLBAR_POPPER_HIDE_DELAY, DEFAULT_TOOLBAR_POPPER_SHOW_DELAY } from '../config';
import { ToolbarIconButton } from '../top/ToolbarIconButton';

interface RenderModeToolbarMenuContextValue {
  activeMenu: string | null;
  setActiveMenu: (menuKey: string) => void;
  closeActiveMenu: () => void;
}

const RenderModeToolbarMenuContext = createContext<RenderModeToolbarMenuContextValue>({
  activeMenu: null,
  setActiveMenu: () => {},
  closeActiveMenu: () => {},
});

export function useRenderModeToolbarMenuContext() {
  return useContext(RenderModeToolbarMenuContext);
}

export function RenderModeToolbarMenuContextProvider({ children }: { children: ReactNode }) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeActiveMenu = useCallback(() => setActiveMenu(null), []);

  const value = useMemo(
    () => ({ activeMenu, setActiveMenu, closeActiveMenu }) satisfies RenderModeToolbarMenuContextValue,
    [activeMenu, setActiveMenu, closeActiveMenu],
  );

  return <RenderModeToolbarMenuContext.Provider value={value}>{children}</RenderModeToolbarMenuContext.Provider>;
}

interface RenderModeToolbarButtonProps extends PropsWithChildren {
  menuKey: string;

  title: string;
  icon: ReactElement;

  disabled?: boolean;

  onMouseClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLElement>) => void;
  onWheel?: (event: React.WheelEvent<HTMLElement>) => void;

  afterButton?: ReactNode;
}

// IMPORTANT: If you make any changes to the event handlers in this component, please ensure you test with both a mouse
//   and a touchscreen device.
//
// When the button is touched we want this to behave like a normal menu -- opening on "click" and closing when a
//   selection is made or a click-away occurs. With a mouse, however, menus are shown on hover and other special cases
//   apply like clicking or scrolling to cycle through options, and middle clicking to toggle visibility.

export function RenderModeToolbarButton({
  menuKey,
  icon,
  title,
  disabled,
  children,
  onMouseClick,
  onMouseUp,
  onWheel,
  afterButton,
}: RenderModeToolbarButtonProps) {
  const context = useContext(RenderModeToolbarMenuContext);
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(null);

  const isOpen = context.activeMenu === menuKey;

  const showMenuOperation = useDelayedOperation(() => {
    context.setActiveMenu(menuKey);
  }, DEFAULT_TOOLBAR_POPPER_SHOW_DELAY);

  const hideMenuOperation = useDelayedOperation(() => {
    context.closeActiveMenu();
  }, DEFAULT_TOOLBAR_POPPER_HIDE_DELAY);

  // ensure we don't have a close operation scheduled when a different menu opens.
  useEffect(() => {
    hideMenuOperation.cancel();
  }, [hideMenuOperation, context.activeMenu]);

  return (
    <>
      <ToolbarIconButton
        ref={setButtonElement}
        title={title}
        disableTooltip
        clickableDuringPicking
        icon={icon}
        disabled={disabled}
        sx={{ touchAction: 'none' }}
        onPointerEnter={(event) => {
          if (isTouchEvent(event.nativeEvent)) return;

          showMenuOperation.cancel();
          hideMenuOperation.cancel();

          if (context.activeMenu == null) {
            showMenuOperation.schedule();
          } else if (context.activeMenu !== menuKey) {
            showMenuOperation.runImmediately(); // no delay when simply dragging over from a different menu button.
          }
        }}
        onPointerLeave={(event) => {
          if (isTouchEvent(event.nativeEvent)) return;

          showMenuOperation.cancel();
          hideMenuOperation.cancel();

          if (isOpen) {
            hideMenuOperation.schedule();
          }
        }}
        onMouseUp={onMouseUp}
        onWheel={onWheel}
        onClick={(event) => {
          if (isTouchEvent(event.nativeEvent)) {
            showMenuOperation.runImmediately();
          } else if (onMouseClick) {
            onMouseClick(event);
          }
        }}
      >
        <Popper open={isOpen} anchorEl={buttonElement} placement="bottom-start">
          <ClickAwayListener
            onClickAway={(event) => {
              // relevant for touchscreen devices; don't remove this because it's useless on your computer please :)
              // don't run on mouse events since clicking the button itself will trigger this.
              if (window.TouchEvent && event instanceof TouchEvent && isOpen) {
                // scheduling hide for the next frame instead of running immediately leads to a smoother transition
                //   between menus as an intermediate no-menu state is avoided.
                hideMenuOperation.schedule({ delay: 0 });
              }
            }}
          >
            <Paper
              className="clickable-during-picking"
              onClick={(event) => {
                // don't want menu item clicks to register on the button itself
                event.stopPropagation();
              }}
              onPointerEnter={(event) => {
                if (isTouchEvent(event.nativeEvent)) return;
                hideMenuOperation.cancel();
              }}
              onPointerLeave={(event) => {
                if (isTouchEvent(event.nativeEvent)) return;
                hideMenuOperation.schedule();
              }}
            >
              <MenuList dense sx={{ paddingY: 1, '> li': { paddingX: 1 } }}>
                {children}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </ToolbarIconButton>

      {afterButton}
    </>
  );
}
